import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useLang } from "../../context/LangContext";
import { useAuth } from "../../context/AuthContext";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import CurrencySwitcher from "../../components/CurrencySwitcher";
import { LayoutDashboard, Users, ShieldCheck, LogOut, Home, CreditCard, KeyRound, Settings, MessageSquare, UserCog, HandCoins } from "lucide-react";

export default function AdminLayout({ children }) {
  const { t } = useLang();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const items = [
    { to: "/admin", label: t("admin_stats"), icon: LayoutDashboard, testid: "nav-admin-stats" },
    { to: "/admin/activations", label: t("activation_requests"), icon: CreditCard, testid: "nav-admin-activations" },
    { to: "/admin/payments", label: t("payments_analytics"), icon: HandCoins, testid: "nav-admin-payments" },
    { to: "/admin/users", label: t("users_list"), icon: Users, testid: "nav-admin-users" },
    { to: "/admin/password-resets", label: t("password_reset_requests"), icon: KeyRound, testid: "nav-admin-passwords" },
    { to: "/admin/approvals", label: t("approvals"), icon: ShieldCheck, testid: "nav-admin-approvals" },
    { to: "/admin/feedback", label: t("admin_feedback"), icon: MessageSquare, testid: "nav-admin-feedback" },
    { to: "/admin/admins", label: t("manage_admins"), icon: UserCog, testid: "nav-admin-admins" },
    { to: "/admin/settings", label: t("admin_settings"), icon: Settings, testid: "nav-admin-settings" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen grain-bg">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <NavLink to="/admin" className="flex items-center gap-2 font-semibold text-[#2A5948]">
            <Home size={20} />
            <span className="text-lg">{t("brand")} — {t("admin")}</span>
          </NavLink>
          <div className="flex items-center gap-3">
            <CurrencySwitcher />
            <LanguageSwitcher />
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E5E1D8] bg-white/70 text-sm"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">{t("logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <nav className="hidden lg:block">
          <div className="sticky top-24 bg-white rounded-2xl border border-[#E5E1D8] p-2 shadow-sm">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/admin"}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mb-1 transition-colors ${
                    isActive ? "bg-[#2A5948] text-white" : "text-[#1C1E17] hover:bg-[#F3EFE7]"
                  }`
                }
              >
                <it.icon size={18} />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <main className="min-w-0">
          <div className="lg:hidden mb-4 flex gap-2 overflow-x-auto pb-2">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/admin"}
                data-testid={`${it.testid}-mobile`}
                className={({ isActive }) =>
                  `whitespace-nowrap flex items-center gap-2 px-3 py-2 rounded-full text-sm border ${
                    isActive ? "bg-[#2A5948] text-white border-[#2A5948]" : "bg-white text-[#1C1E17] border-[#E5E1D8]"
                  }`
                }
              >
                <it.icon size={14} />
                {it.label}
              </NavLink>
            ))}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}
